
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, ArrowRight, Loader2 } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const Verify = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isProcessingLink, setIsProcessingLink] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { verifyOtp, isLoading, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Process URL params and handle auto-verification
  useEffect(() => {
    const processUrlParams = async () => {
      // Check for email in state (from signup flow or login redirect)
      const stateEmail = location.state?.email;
      if (stateEmail) {
        setEmail(stateEmail);
        // Do NOT automatically send verification email when redirected from login
        // Only send when user explicitly requests it
      }
      
      // Check for token in URL (from email verification link)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      
      // If there's a hash with the verification code, extract it
      const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
      const typeFromHash = hashParams.get('type');
      const tokenFromHash = hashParams.get('token');
      const emailFromHash = hashParams.get('email');
      
      // If we have all the necessary info for auto-verification
      if ((tokenFromUrl || (typeFromHash === 'signup' && tokenFromHash)) && (stateEmail || emailFromHash)) {
        setIsProcessingLink(true);
        
        try {
          const verifyEmail = stateEmail || emailFromHash;
          const verifyToken = tokenFromUrl || tokenFromHash;
          
          if (verifyEmail && verifyToken) {
            await verifyOtp(verifyEmail, verifyToken);
            // if successful, we'll be redirected by the verifyOtp function
          }
        } catch (error) {
          console.error("Auto-verification failed:", error);
          setIsProcessingLink(false);
          // If we have email from hash, use it to pre-fill
          if (emailFromHash) {
            setEmail(emailFromHash);
          }
        }
      }
    };
    
    processUrlParams();
  }, [location, verifyOtp]);

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      return;
    }
    
    if (otp.length !== 6) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await verifyOtp(email, otp);
      // Navigation is handled in verifyOtp function
    } catch (error) {
      console.error("Verification error:", error);
      // Error handled in verifyOtp function
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email) {
      return;
    }
    
    setIsResending(true);
    try {
      await resendVerificationEmail(email);
    } catch (error) {
      console.error("Resend verification error:", error);
      // Error handled in resendVerificationEmail function
    } finally {
      setIsResending(false);
    }
  };

  if (isProcessingLink) {
    return (
      <div className="min-h-screen bg-[#375062] flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src="/icon-mark.png" alt="Doyence" className="h-10 w-10" />
            <h1 className="text-3xl font-bold text-white">
              Doyence
            </h1>
          </div>
          <h2 className="text-center text-xl font-semibold text-white mb-8">
            Verifying your email...
          </h2>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#375062] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex items-center justify-center gap-3 mb-2">
          <img src="/icon-mark.png" alt="Doyence" className="h-10 w-10" />
          <h1 className="text-3xl font-bold text-white">
            Doyence
          </h1>
        </div>
        <h2 className="text-center text-xl font-semibold text-white">
          Verify Your Email
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary-50 flex items-center justify-center">
              <Mail className="h-6 w-6 text-primary-600" />
            </div>
            <CardTitle className="text-center">Verification required</CardTitle>
            <CardDescription className="text-center">
              {email ? (
                <>
                  We've sent a 6-digit verification code to your email
                  <span className="font-medium block">{email}</span>
                </>
              ) : (
                "Enter your email and we'll send you a 6-digit verification code"
              )}
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleVerifyOtp}>
            <CardContent className="space-y-4">
              {!email && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="otp">Verification code</Label>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              
              <div className="text-sm text-center">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                  disabled={isResending || !email}
                >
                  {isResending ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    "Didn't receive a code? Resend"
                  )}
                </button>
              </div>
            </CardContent>
            
            <CardFooter>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSubmitting || isLoading || otp.length !== 6 || !email}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Verify Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Verify;
